import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addAditionalNote,
  addAdvancedDiagnose,
  addDiagnoseNote,
  addDiagnoses,
} from "../../../../../redux/diagnosestate/diagnoseAction";
import { updateAdvancedDiagnoseAction } from "../../../../../redux/diagnosestate/diagnoseAction";
import TreatTraw from "./TreatTraw";
import { RootState } from "../../../../../redux/store";
import baseUrl from "../../../../../api/baseUrl";

function Treatdiagnose({ adult }: { adult: boolean }) {
  const dispatch = useDispatch();
  const teeth = useSelector((state: RootState) => state.diagnose.diagnoseTooth);
  const advancedDiagnose = useSelector((state: RootState) => state.diagnose.advancedDiagnose);
  const { ID } = useParams();

  useEffect(() => {
    baseUrl
      .get("treatment/find-treatment", {
        headers: {
          treatmentid: ID,
        },
      })
      .then((response) => {
        dispatch(addAditionalNote(response.data.data.aditionalNote));
        dispatch(addDiagnoseNote(response.data.data.patientComplain));
        dispatch(addDiagnoses(response.data.data.diagnose));
        dispatch(addAdvancedDiagnose(response.data.data.advancedDiagnose ?? []));
      })
      .catch((error) => {
        console.log(error);
      });
  }, [ID, dispatch]);

  const advancedDiagnoseRows = useMemo(() => {
    if (!Array.isArray(advancedDiagnose)) return [];
    const valid = advancedDiagnose.filter(
      (adv: any) => adv.toothNo !== undefined && typeof adv.toothNo === "number"
    );
    const rows: any[] = [];
    const hasBridge = (b: any) =>
      b && b.bridgeType && (b.lefttooth != null || b.righttooth != null || b.bridgeType);
    const formatBridge = (b: any, toothNo: number) => {
      if (!b || !b.bridgeType) return "-";
      const type =
        b.bridgeType === "conventional-fixed"
          ? "Conventional Fixed"
          : b.bridgeType === "resin-bonded"
            ? "Resin Bonded"
            : b.bridgeType === "cantilever"
              ? "Cantilever"
              : b.bridgeType;
      const parts = [type];
      if (b.lefttooth != null) parts.push(String(b.lefttooth));
      parts.push(String(toothNo));
      if (b.righttooth != null) parts.push(String(b.righttooth));
      return parts.join("–");
    };
    valid.forEach((adv: any) => {
      if (adv.surface && Array.isArray(adv.surface) && adv.surface.length > 0) {
        adv.surface.forEach((surface: any) => {
          rows.push({
            type: "advanced",
            toothNo: adv.toothNo,
            diagnose: surface.diagnose || "-",
            surface: surface.sid || "-",
            note: adv.note || "-",
            action: adv.action,
            bridgeLabel: formatBridge(adv.bridge, adv.toothNo),
            _id: `adv-${adv.toothNo}-${surface.sid}`,
          });
        });
      } else {
        const hasOther =
          adv.condition ||
          adv.note ||
          adv.rootDiagnoses ||
          (adv.rootVariantDiagnoses && adv.rootVariantDiagnoses.length > 0) ||
          hasBridge(adv.bridge);
        if (hasOther) {
          rows.push({
            type: "advanced",
            toothNo: adv.toothNo,
            diagnose: adv.condition || (hasBridge(adv.bridge) ? adv.bridge?.bridgeType || "Bridge" : "-"),
            surface: "-",
            note: adv.note || "-",
            action: adv.action,
            bridgeLabel: formatBridge(adv.bridge, adv.toothNo),
            _id: `adv-${adv.toothNo}`,
          });
        }
      }
    });
    return rows;
  }, [advancedDiagnose]);

  const filteredTeeth = teeth.filter((item: any) => item.adult === adult);
  const allDiagnoses = [...filteredTeeth, ...advancedDiagnoseRows];

  const onAdvancedActionChange = (toothNo: number, checked: boolean) => {
    baseUrl
      .post(
        "treatment/add-advanced-diagnose-action",
        { toothNo, action: checked },
        { headers: { treatmentid: ID } }
      )
      .then((response) => {
        if (response.data?.success) {
          dispatch(updateAdvancedDiagnoseAction({ toothNo, action: checked }));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Diagnosis Table</h3>
      </div>
      <div className="p-3 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-700/50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Diagnose</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Tooth No</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Surface</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Note</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Est. Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {allDiagnoses.map((item: any, i: number) =>
              item.type === "advanced" ? (
                <AdvancedActionRow
                  key={item._id || i}
                  item={item}
                  onAdvancedActionChange={onAdvancedActionChange}
                />
              ) : (
                <TreatTraw item={item} key={item._id || i} />
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvancedActionRow({
  item,
  onAdvancedActionChange,
}: {
  item: any;
  onAdvancedActionChange: (toothNo: number, checked: boolean) => void;
}) {
  const [check, setcheck] = React.useState(!item.action);
  React.useEffect(() => {
    setcheck(!item.action);
  }, [item.action]);

  const onActionChange = (e: any) => {
    const checked = e.target.checked;
    setcheck(!checked);
    onAdvancedActionChange(item.toothNo, checked);
  };

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 bg-blue-50/30 dark:bg-blue-900/20 transition-colors">
      <td className="px-4 py-3">
        <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">Advanced</span>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">{item.diagnose}</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{item.toothNo}</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {item.surface !== "-" ? item.surface : item.bridgeLabel !== "-" ? item.bridgeLabel : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 max-w-[150px] truncate">{item.note}</td>
      <td className="px-4 py-3">—</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">—</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${check ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500"}`}>No</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!check}
              onChange={onActionChange}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
          </label>
          <span className={`text-xs font-medium ${!check ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500"}`}>Yes</span>
        </div>
      </td>
    </tr>
  );
}

export default Treatdiagnose;
